import { createSelector } from '@reduxjs/toolkit';
import {
  AlphaQuoteSyncStatus,
  type AutoConfiguratorState,
  LineItemDisplayStatus,
  type QuantityBreak,
} from '@snap-types/models/auto-configurator/state';
import { lineItemColumns } from '@ui/features/alpha/config/columns';
import { operationNoteKeys } from '@ui/features/alpha/config/operation-notes';
import { getStrandedOperationNoteKeys } from '@ui/features/alpha/logic/operation-notes';
import { selectFields } from '@ui/features/alpha/state/selectors/field';
import { selectFirstQuantityBreak } from '@ui/features/alpha/state/selectors/quantity-break';
import { fieldIdToValueId } from '@ui/features/alpha/utilities/field-id';

import { getLineItem } from '../../logic/line-item';
import { deriveLineItemDisplayStatus } from './helpers/line-item-display-status';

export function selectLineItemUuids(state: AutoConfiguratorState) {
  return state.lineItemUuids;
}

export const selectLineItems = createSelector(
  [
    (state: AutoConfiguratorState) => state.lineItemUuids,
    (state: AutoConfiguratorState) => state.lineItems,
  ],
  (lineItemUuids, lineItems) => lineItemUuids.map((uuid) => lineItems[uuid]),
);

export const selectHasPreviouslyAppliedLineItems = createSelector(
  [
    (state: AutoConfiguratorState) => state.lineItemUuids,
    (state: AutoConfiguratorState) => state.lineItems,
  ],
  (lineItemUuids, lineItems) =>
    lineItemUuids.some((uuid) => {
      const lineItem = lineItems[uuid];

      if (!lineItem) {
        return false;
      }

      return (
        lineItem.snapQuoteLineUuid !== null ||
        lineItem.lastAppliedAt !== null ||
        lineItem.syncStatus !== AlphaQuoteSyncStatus.UNSYNCED
      );
    }),
);

export function selectLineItem(
  state: AutoConfiguratorState,
  lineItemUuid: string,
) {
  return getLineItem(state, lineItemUuid);
}

const lineItemFieldIds = lineItemColumns.map((column) => column.fieldId);

export const selectLineItemCells = createSelector(
  [
    (state: AutoConfiguratorState, lineItemUuid: string) =>
      selectFirstQuantityBreak(state, lineItemUuid).uuid,
    (_: AutoConfiguratorState, lineItemUuid: string) => lineItemUuid,
    (state: AutoConfiguratorState, _: string) =>
      selectFields(state, lineItemFieldIds),
  ],
  (firstQuantityBreakUuid, lineItemUuid, fields) =>
    lineItemColumns.map((column, index) => {
      const uuid =
        column.fieldId.model === 'lineItem'
          ? lineItemUuid
          : firstQuantityBreakUuid;

      return {
        column,
        field: fields[index],
        valueId: fieldIdToValueId(column.fieldId, uuid),
      };
    }),
);

export function selectLineItemCalculatedDescription(
  state: AutoConfiguratorState,
  lineItemUuid: string,
) {
  return (
    getLineItem(state, lineItemUuid).calculatedData?.calculatedDescription ?? ''
  );
}

export function selectLineItemDimensionUnit(
  state: AutoConfiguratorState,
  lineItemUuid: string,
) {
  return getLineItem(state, lineItemUuid).dimensionUnit;
}

export function selectLineItemHasNotes(
  state: AutoConfiguratorState,
  lineItemUuid: string,
) {
  const lineItem = getLineItem(state, lineItemUuid);

  return (
    !!lineItem.notes.value ||
    operationNoteKeys.some((key) => !!lineItem[key].value)
  );
}

/**
 * True when a note is written for an operation the line does not build, which
 * an apply would leave behind. Reported as a count rather than the keys so the
 * result stays referentially stable across store updates.
 */
export function selectLineItemStrandedNoteCount(
  state: AutoConfiguratorState,
  lineItemUuid: string,
) {
  return getStrandedOperationNoteKeys(getLineItem(state, lineItemUuid)).length;
}

export function selectLineItemAttachments(
  state: AutoConfiguratorState,
  lineItemUuid: string,
) {
  return getLineItem(state, lineItemUuid).attachments;
}

export function selectLineItemStatus(
  state: AutoConfiguratorState,
  lineItemUuid: string,
) {
  return getLineItem(state, lineItemUuid).status;
}

export function selectLineItemEvaluationStatus(
  state: AutoConfiguratorState,
  lineItemUuid: string,
) {
  return getLineItem(state, lineItemUuid).evaluationStatus;
}

export function selectLineItemSyncStatus(
  state: AutoConfiguratorState,
  lineItemUuid: string,
) {
  return getLineItem(state, lineItemUuid).syncStatus;
}

export function selectLineItemSnapQuoteLineUuid(
  state: AutoConfiguratorState,
  lineItemUuid: string,
) {
  return getLineItem(state, lineItemUuid).snapQuoteLineUuid;
}

export function selectLineItemLastAppliedAt(
  state: AutoConfiguratorState,
  lineItemUuid: string,
) {
  return getLineItem(state, lineItemUuid).lastAppliedAt;
}

export function selectLineItemLastApplyFailedAt(
  state: AutoConfiguratorState,
  lineItemUuid: string,
) {
  return getLineItem(state, lineItemUuid).lastApplyFailedAt;
}

export function selectIsLineItemAwaitingLatestCalculation(
  state: AutoConfiguratorState,
  lineItemUuid: string,
) {
  return Boolean(state.pendingCalculationRequestIdByLineItemUuid[lineItemUuid]);
}

export function selectLineItemCalculationError(
  state: AutoConfiguratorState,
  lineItemUuid: string,
) {
  return state.calculationErrorsByLineItemUuid[lineItemUuid] ?? null;
}

function selectLineItemQuantityBreakUuids(
  state: AutoConfiguratorState,
  lineItemUuid: string,
) {
  return getLineItem(state, lineItemUuid).quantityBreakUuids;
}

export const selectLineItemDisplayStatus = createSelector(
  [
    selectLineItem,
    selectLineItemQuantityBreakUuids,
    (state: AutoConfiguratorState) => state.quantityBreaks,
    selectIsLineItemAwaitingLatestCalculation,
    selectLineItemCalculationError,
  ],
  (
    lineItem,
    quantityBreakUuids,
    quantityBreaksByUuid,
    isAwaitingLatestCalculation,
    calculationError,
  ): LineItemDisplayStatus => {
    const quantityBreaks = quantityBreakUuids
      .map((uuid) => quantityBreaksByUuid[uuid])
      .filter((quantityBreak): quantityBreak is QuantityBreak =>
        Boolean(quantityBreak),
      );

    return deriveLineItemDisplayStatus(
      lineItem,
      quantityBreaks,
      isAwaitingLatestCalculation,
      calculationError,
    );
  },
);

export const selectAutoConfiguratorSaveStatus = createSelector(
  [
    selectLineItemUuids,
    (state: AutoConfiguratorState) => state.lineItems,
    (state: AutoConfiguratorState) => state.quantityBreaks,
    (state: AutoConfiguratorState) =>
      state.pendingCalculationRequestIdByLineItemUuid,
    (state: AutoConfiguratorState) => state.calculationErrorsByLineItemUuid,
    (state: AutoConfiguratorState) => state.lastSaveDateTime,
  ],
  (
    lineItemUuids,
    lineItems,
    quantityBreaks,
    pendingCalculationRequestIdByLineItemUuid,
    calculationErrorsByLineItemUuid,
    lastSaved,
  ) => {
    let issueCount = 0;

    for (const lineItemUuid of lineItemUuids) {
      const lineItem = lineItems[lineItemUuid];

      if (!lineItem) {
        continue;
      }

      const lineQuantityBreaks = lineItem.quantityBreakUuids
        .map((uuid) => quantityBreaks[uuid])
        .filter((quantityBreak): quantityBreak is QuantityBreak =>
          Boolean(quantityBreak),
        );

      const status = deriveLineItemDisplayStatus(
        lineItem,
        lineQuantityBreaks,
        Boolean(pendingCalculationRequestIdByLineItemUuid[lineItemUuid]),
        calculationErrorsByLineItemUuid[lineItemUuid],
      );

      if (lineItemNeedsAttention(status)) {
        issueCount += 1;
      }
    }

    return {
      hasLineItems: lineItemUuids.length > 0,
      issueCount,
      lastSaved,
    };
  },
);

function lineItemNeedsAttention(status: LineItemDisplayStatus): boolean {
  return (
    status === 'customer-requires-sems' ||
    status === 'calculation-error' ||
    status === 'consult-engineering' ||
    status === 'incomplete'
  );
}
